import NotificationEvent from '@interfaces/NotificationEvent';
import { ILibraryApiClient, ILibraryEntry, ILibraryEntryFilter, LibraryEntryFilter, LibraryEntryView } from '@cyber-range/cyber-range-api-ctf-library-client';
import Config from '@/config';
import { useNotificationStore } from '@stores/notificationStore';
import { useApiClientStore } from './apiClientStore';
import { defineStore } from 'pinia';
import { useToMap } from '@/composables/useToMap';

export const useLibraryEntryStore = defineStore('libraryEntryStore', 
{
    state: () =>
    ({
        _initialLoad:undefined as Promise<void>|undefined,
        isLibraryEntriesFetched: false,
        _libraryEntries: {} as Record<string, ILibraryEntry>,
        _libraryCategories: new Set<string>(),
        deletedEntries: [] as string[]
    }),
    getters:
    {
        libraryEntries(): ILibraryEntry[]
        {
            return Object.values(this._libraryEntries);
        },
        sortedLibraryEntries(): ILibraryEntry[]
        {
            return [...this.libraryEntries].sort((a,b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
        },
        libraryCategories(): string[]
        {
            return [...this._libraryCategories].filter(c=>!! c).sort((a,b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
        },
        libraryAuthors(): {name: string, email: string}[]
        {
            const authors: Record<string, {name: string, email: string}> = {};

            for (const { protectedInformation } of this.libraryEntries)
            {
                if (protectedInformation?.authorName && protectedInformation?.authorEmail)
                {
                    const key = protectedInformation.authorName + protectedInformation.authorEmail;
                    authors[key] ??= {
                        name: protectedInformation.authorName,
                        email: protectedInformation.authorEmail
                    };
                }
            }

            return Object.values(authors);
        },
        initialLoad():Promise<void>
        {
            return this._initialLoad;
        }
    },
    actions:
    {
        setLibraryEntries(libraryEntries:ILibraryEntry[]): void
        {
            this._libraryEntries = libraryEntries.reduce((allEntries, currentEntry)=>
                {
                    const existingEntry = this._libraryEntries[currentEntry.id]
                    const lastestEntry = (
                            !!existingEntry?.modifiedTimestamp &&
                            !!currentEntry.modifiedTimestamp &&
                            existingEntry.modifiedTimestamp > currentEntry.modifiedTimestamp
                        )
                            ? existingEntry
                            : currentEntry 
                    allEntries[currentEntry.id] = lastestEntry;
                    return allEntries; 
                }
            ,{});

            // handle background refresh occuring simultaneously with delete one entry
            do
            {
                delete this._libraryEntries[this.deletedEntries.pop()];
            }while(this.deletedEntries.length);

            this._libraryCategories = new Set<string>([...libraryEntries.map(e=> e.category)]);
            this.isLibraryEntriesFetched = true;
        },
        setLibraryEntry(libraryEntry: ILibraryEntry): void
        {
            this._libraryEntries[libraryEntry.id] = libraryEntry;
            this._libraryCategories.add(libraryEntry.category);
        },
        clearLibraryEntry(id: string|string[]): void
        {
            const ids = Array.isArray(id) ? id : [id];
            for (let id of ids)
            {
                this.deletedEntries.push(id);
                delete this._libraryEntries[id];
            }
        },
        clearLibraryEntries(): void
        {
            this._libraryEntries = {};
            this._libraryCategories = new Set<string>();
            this.isLibraryEntriesFetched = false;
        },
        async fetchLibraryEntries(payload?: {background: boolean}): Promise<void> 
        {
            const client: ILibraryApiClient = payload?.background ? useApiClientStore().backgroundLibraryApiClient : useApiClientStore().libraryApiClient;
            const libraryEntries:ILibraryEntry[] = [];

            let filter: ILibraryEntryFilter = new LibraryEntryFilter({ limit: Config.LIBRARY_FETCH_SIZE, view: LibraryEntryView.Full });
            const fetchTime = new Date().toISOString();
            do
            {
                const result = await client.get(filter);
                filter.token = result.nextPageToken;
                libraryEntries.push(...result.items);
            }
            while(filter.token)

            const additions = Object.fromEntries(useToMap<ILibraryEntry>(this.libraryEntries.filter((e:ILibraryEntry)=>e.createdTimestamp >= fetchTime)));
            const fetchedEntries = Object.fromEntries(useToMap<ILibraryEntry>(libraryEntries));
            this.setLibraryEntries(Object.values({...additions,...fetchedEntries}));

            if(!useNotificationStore().isSubscribed(NotificationEvent.libraryEntriesUpdated))
            {
                useNotificationStore().subscribe({
                    event: NotificationEvent.libraryEntriesUpdated, 
                    callback: () => this.fetchLibraryEntries({ background: true })
                }); 
            };
        },

        preloadLibraryEntries(delay:number = Config.LIBRARY_INITIAL_LOAD_DELAY_IN_MS)
        {
            if(!this._initialLoad)
            {
                this._initialLoad = new Promise<void>((resolve,reject) =>
                {
                    setTimeout(() =>
                    {
                        this.fetchLibraryEntries({ background: true })
                            .then(()=>{ return resolve();})
                            .catch((e:Error)=>{ return reject(e);});
                    }, delay);
                });
            }
        },  
    }
});
